import './styles.scoped.css'
import {Typography, Avatar} from '@mui/material'
import {CheckBox, Warning} from '@mui/icons-material'
import {getColorFromName, getInitials, formatTime, capitalizeFirst, ignoreTimezone} from '../../../../utils/helpers'
import {format} from 'date-fns'
import {SkeletonContainer, TextSkeleton, AvatarSkeleton} from '../../../../components'

// Logs from a schedule change don't carry the client; the page passes it in.
const scheduledFor = (item, clientName) => {
    const name = item?.metadata?.client_name || clientName
    return name ? ` for ${name}` : ''
}

const EntriesSkeleton = () => (
    <SkeletonContainer>
        {[
            ['70%', '55%'],
            ['62%', '78%'],
            ['74%', '50%'],
            ['58%', '66%'],
        ].map(([title, detail], index) => (
            <div key={index}>
                {index > 0 && <div className="v-line"></div>}
                <div className="entry">
                    <AvatarSkeleton size={24} />
                    <div className="entry-content">
                        <TextSkeleton width={130} />
                        <TextSkeleton width={title} fontSize={15} />
                        <TextSkeleton width={detail} />
                        <TextSkeleton width={120} fontSize={12} />
                    </div>
                </div>
            </div>
        ))}
    </SkeletonContainer>
)

export default ({items, clientName, skeleton = false}) => (
    <div className="card">
        <Typography variant="h8" color="text.primary">
            ACTIVITY LOG
        </Typography>
        <br />
        <br />
        {skeleton && <EntriesSkeleton />}
        {!skeleton &&
            items?.map((item, index) => (
                <div key={index}>
                    {item?.event_type == 'clock_in' ? (
                        <>
                            {index > 0 && <div className="v-line"></div>}
                            <div className="entry">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 24, height: 24}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.actor_name),
                                            width: 24,
                                            height: 24,
                                            fontSize: 12,
                                        }}>
                                        {getInitials(item?.actor_name)}
                                    </Avatar>
                                )}
                                <div className="entry-content">
                                    <Typography variant="body2" color="text.primary">
                                        {item?.actor_name} (Caregiver)
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Clocked In (Visit Started)
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" style={{maxWidth: 220}}>
                                        Caregiver arrived at : {item?.metadata?.clock_in_address}
                                    </Typography>
                                    {item?.metadata?.clock_in_warnings?.includes('late') ? (
                                        <div className="content">
                                            <Warning color="warning" fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Clocked in later than scheduled
                                            </Typography>
                                        </div>
                                    ) : (
                                        <div className="content">
                                            <CheckBox color="success" fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Same as scheduled time
                                            </Typography>
                                        </div>
                                    )}
                                    {item?.metadata?.clock_in_warnings?.includes('outside_radius') ? (
                                        <div className="content">
                                            <Warning color="warning" fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Clocked in location outside radius of scheduled location
                                            </Typography>
                                        </div>
                                    ) : (
                                        <div className="content">
                                            <CheckBox color="success" fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Same as scheduled location
                                            </Typography>
                                        </div>
                                    )}
                                    {item?.metadata?.clock_in_reason && (
                                        <div className="reason-card">
                                            <Typography variant="caption" color="text.primary">
                                                Reason:
                                            </Typography>
                                            <br />
                                            <Typography variant="caption" color="text.primary">
                                                {item?.metadata?.clock_in_reason}
                                            </Typography>
                                        </div>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} at{' '}
                                        {format(new Date(item?.created_at ?? null), 'h:mm a')}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : item?.event_type == 'clock_out' ? (
                        <>
                            {index > 0 && <div className="v-line"></div>}
                            <div className="entry">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 24, height: 24}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.actor_name),
                                            width: 24,
                                            height: 24,
                                            fontSize: 12,
                                        }}>
                                        {getInitials(item?.actor_name)}
                                    </Avatar>
                                )}
                                <div className="entry-content">
                                    <Typography variant="body2" color="text.primary">
                                        {item?.actor_name} (Caregiver)
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Clocked Out (Visit Ended)
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Caregiver finished the visit at {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} at{' '}
                                        {format(new Date(item?.created_at ?? null), 'h:mm a')}.
                                    </Typography>
                                    {item?.metadata?.clock_out_warnings?.includes('early') ? (
                                        <div className="content">
                                            <Warning color="warning" fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Clocked out earlier than scheduled
                                            </Typography>
                                        </div>
                                    ) : item?.metadata?.clock_out_warnings?.includes('late') ? (
                                        <div className="content">
                                            <Warning color="warning" fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Clocked out later than scheduled
                                            </Typography>
                                        </div>
                                    ) : (
                                        <div className="content">
                                            <CheckBox color="success" fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Same as scheduled time
                                            </Typography>
                                        </div>
                                    )}
                                    {item?.metadata?.clock_out_warnings?.includes('outside_radius') ? (
                                        <div className="content">
                                            <Warning color="warning" fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Clocked out location outside radius of scheduled location
                                            </Typography>
                                        </div>
                                    ) : (
                                        <div className="content">
                                            <CheckBox color="success" fontSize="small" />
                                            <Typography variant="body2" color="text.secondary">
                                                Same as scheduled location
                                            </Typography>
                                        </div>
                                    )}
                                    {item?.metadata?.clock_out_reason && (
                                        <div className="reason-card">
                                            <Typography variant="caption" color="text.primary">
                                                Reason:
                                            </Typography>
                                            <br />
                                            <Typography variant="caption" color="text.primary">
                                                {item?.metadata?.clock_out_reason}
                                            </Typography>
                                        </div>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} at{' '}
                                        {format(new Date(item?.created_at ?? null), 'h:mm a')}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : item?.event_type == 'visit_created' ? (
                        <>
                            {index > 0 && <div className="v-line"></div>}
                            <div className="entry">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 24, height: 24}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.actor_name),
                                            width: 24,
                                            height: 24,
                                            fontSize: 12,
                                        }}>
                                        {getInitials(item?.actor_name)}
                                    </Avatar>
                                )}
                                <div className="entry-content">
                                    <Typography variant="body2" color="text.primary">
                                        {item?.actor_name} (Admin)
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Created Schedule
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Visit scheduled{scheduledFor(item, clientName)} on{' '}
                                        {format(new Date(ignoreTimezone(item?.metadata?.date)), 'MMM dd, yyyy')} at{' '}
                                        {formatTime(item?.metadata?.start_time)}
                                    </Typography>
                                    {item?.metadata?.reason && (
                                        <div className="reason-card">
                                            <Typography variant="caption" color="text.primary">
                                                Reason:
                                            </Typography>
                                            <br />
                                            <Typography variant="caption" color="text.primary">
                                                {item?.metadata?.reason}
                                            </Typography>
                                        </div>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} at{' '}
                                        {format(new Date(item?.created_at ?? null), 'h:mm a')}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : item?.event_type == 'caregiver_signed' ? (
                        <>
                            {index > 0 && <div className="v-line"></div>}
                            <div className="entry">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 24, height: 24}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.actor_name),
                                            width: 24,
                                            height: 24,
                                            fontSize: 12,
                                        }}>
                                        {getInitials(item?.actor_name)}
                                    </Avatar>
                                )}
                                <div className="entry-content">
                                    <Typography variant="body2" color="text.primary">
                                        {item?.actor_name} (Caregiver)
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Submitted Signature
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Caregiver has signed to confirm visit completion.
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} at{' '}
                                        {format(new Date(item?.created_at ?? null), 'h:mm a')}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : item?.event_type == 'client_signed' ? (
                        <>
                            {index > 0 && <div className="v-line"></div>}
                            <div className="entry">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 24, height: 24}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.actor_name),
                                            width: 24,
                                            height: 24,
                                            fontSize: 12,
                                        }}>
                                        {getInitials(item?.actor_name)}
                                    </Avatar>
                                )}
                                <div className="entry-content">
                                    <Typography variant="body2" color="text.primary">
                                        {item?.actor_name} (Client)
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Submitted Signature
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Client has signed to confirm visit completion.
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} at{' '}
                                        {format(new Date(item?.created_at ?? null), 'h:mm a')}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : item?.event_type == 'client_signature_skipped' ? (
                        <>
                            {index > 0 && <div className="v-line"></div>}
                            <div className="entry">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 24, height: 24}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.actor_name),
                                            width: 24,
                                            height: 24,
                                            fontSize: 12,
                                        }}>
                                        {getInitials(item?.actor_name)}
                                    </Avatar>
                                )}
                                <div className="entry-content">
                                    <Typography variant="body2" color="text.primary">
                                        {item?.actor_name} (Caregiver)
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Client Signature Skipped
                                    </Typography>
                                    <div className="reason-card">
                                        <Typography variant="caption" color="text.primary">
                                            Reason:
                                        </Typography>
                                        <br />
                                        <Typography variant="caption" color="text.primary">
                                            {item?.metadata?.reason}
                                        </Typography>
                                    </div>
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} at{' '}
                                        {format(new Date(item?.created_at ?? null), 'h:mm a')}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : item?.event_type == 'visit_edited' ? (
                        <>
                            {index > 0 && <div className="v-line"></div>}
                            <div className="entry">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 24, height: 24}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.actor_name),
                                            width: 24,
                                            height: 24,
                                            fontSize: 12,
                                        }}>
                                        {getInitials(item?.actor_name)}
                                    </Avatar>
                                )}
                                <div className="entry-content">
                                    <Typography variant="body2" color="text.primary">
                                        {item?.actor_name} (Admin)
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Updated Schedule
                                    </Typography>
                                    <div className="reason-card">
                                        <Typography variant="caption" color="text.primary">
                                            Reason:
                                        </Typography>
                                        <br />
                                        {item?.metadata?.reason && (
                                            <>
                                                <Typography variant="caption" color="text.primary">
                                                    {item?.metadata?.reason}
                                                </Typography>
                                                <br />
                                            </>
                                        )}
                                        {item?.metadata?.new_start_time && (
                                            <>
                                                <Typography variant="caption" color="text.primary">
                                                    • Updated time from {formatTime(item?.metadata?.old_start_time)} -{' '}
                                                    {formatTime(item?.metadata?.old_end_time)} to {formatTime(item?.metadata?.new_start_time)} -{' '}
                                                    {formatTime(item?.metadata?.new_end_time)}
                                                </Typography>
                                                <br />
                                            </>
                                        )}
                                        {item?.metadata?.new_caregiver_id && (
                                            <Typography variant="caption" color="text.primary">
                                                • Changed caregiver
                                            </Typography>
                                        )}
                                    </div>
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} at{' '}
                                        {format(new Date(item?.created_at ?? null), 'h:mm a')}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : item?.event_type == 'status_change' && item?.metadata?.new_status == 'cancelled' ? (
                        <>
                            {index > 0 && <div className="v-line"></div>}
                            <div className="entry">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 24, height: 24}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.actor_name),
                                            width: 24,
                                            height: 24,
                                            fontSize: 12,
                                        }}>
                                        {getInitials(item?.actor_name)}
                                    </Avatar>
                                )}
                                <div className="entry-content">
                                    <Typography variant="body2" color="text.primary">
                                        {item?.actor_name} (Admin)
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Cancelled Schedule
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Visit for{' '}
                                        <Typography component="span" variant="body2" color="primary">
                                            {item?.actor_name}
                                        </Typography>{' '}
                                        on {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} was cancelled.
                                    </Typography>
                                    <div className="reason-card">
                                        <Typography variant="caption" color="text.primary">
                                            Reason:
                                        </Typography>
                                        <br />
                                        <Typography variant="caption" color="text.primary">
                                            {item?.metadata?.reason}
                                        </Typography>
                                    </div>
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} at{' '}
                                        {format(new Date(item?.created_at ?? null), 'h:mm a')}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : item?.event_type == 'status_change' && item?.metadata?.new_status == 'completed' ? (
                        <>
                            {index > 0 && <div className="v-line"></div>}
                            <div className="entry">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 24, height: 24}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.actor_name),
                                            width: 24,
                                            height: 24,
                                            fontSize: 12,
                                        }}>
                                        {getInitials(item?.actor_name)}
                                    </Avatar>
                                )}
                                <div className="entry-content">
                                    <Typography variant="body2" color="text.primary">
                                        {item?.actor_name} (Admin)
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Completed Schedule
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Visit for{' '}
                                        <Typography component="span" variant="body2" color="primary">
                                            {item?.actor_name}
                                        </Typography>{' '}
                                        on {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} was completed.
                                    </Typography>
                                    <div className="reason-card">
                                        <Typography variant="caption" color="text.primary">
                                            Reason:
                                        </Typography>
                                        <br />
                                        <Typography variant="caption" color="text.primary">
                                            {item?.metadata?.reason}
                                        </Typography>
                                    </div>
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} at{' '}
                                        {format(new Date(item?.created_at ?? null), 'h:mm a')}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : item?.event_type == 'caregiver_changed' ? (
                        <>
                            {index > 0 && <div className="v-line"></div>}
                            <div className="entry">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 24, height: 24}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.actor_name),
                                            width: 24,
                                            height: 24,
                                            fontSize: 12,
                                        }}>
                                        {getInitials(item?.actor_name)}
                                    </Avatar>
                                )}
                                <div className="entry-content">
                                    <Typography variant="body2" color="text.primary">
                                        {item?.actor_name} (Admin)
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Reassigned Caregiver
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Caregiver changed from{' '}
                                        <Typography component="span" variant="body2" color="primary">
                                            {item?.metadata?.old_caregiver}
                                        </Typography>{' '}
                                        to{' '}
                                        <Typography component="span" variant="body2" color="primary">
                                            {item?.metadata?.new_caregiver}
                                        </Typography>
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} at{' '}
                                        {format(new Date(item?.created_at ?? null), 'h:mm a')}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : item?.event_type == 'visit_rescheduled' ? (
                        <>
                            {index > 0 && <div className="v-line"></div>}
                            <div className="entry">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 24, height: 24}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.actor_name),
                                            width: 24,
                                            height: 24,
                                            fontSize: 12,
                                        }}>
                                        {getInitials(item?.actor_name)}
                                    </Avatar>
                                )}
                                <div className="entry-content">
                                    <Typography variant="body2" color="text.primary">
                                        {item?.actor_name} (Admin)
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        Updated Schedule
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Schedule changed to {format(new Date(ignoreTimezone(item?.metadata?.new_date)), 'MMM dd, yyyy')} at{' '}
                                        {formatTime(item?.metadata?.new_start_time)} - {formatTime(item?.metadata?.new_end_time)}.
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} at{' '}
                                        {format(new Date(item?.created_at ?? null), 'h:mm a')}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {index > 0 && <div className="v-line"></div>}
                            <div className="entry">
                                {item?.avatar_url ? (
                                    <Avatar src={item?.avatar_url} style={{width: 24, height: 24}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.actor_name),
                                            width: 24,
                                            height: 24,
                                            fontSize: 12,
                                        }}>
                                        {getInitials(item?.actor_name)}
                                    </Avatar>
                                )}
                                <div className="entry-content">
                                    <Typography variant="body2" color="text.primary">
                                        {item?.actor_name} ({capitalizeFirst(item?.actor_role)})
                                    </Typography>
                                    <Typography variant="h9" color="text.primary">
                                        {item?.description}
                                    </Typography>
                                    {item?.metadata?.reason && (
                                        <Typography variant="body2" color="text.secondary">
                                            {item?.metadata?.reason}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(item?.created_at ?? null), 'MMM dd, yyyy')} at{' '}
                                        {format(new Date(item?.created_at ?? null), 'h:mm a')}
                                    </Typography>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ))}
    </div>
)
